import { html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";;
import { dom, ColorTypes, Overlay } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FormContainer } from "@omegagrid/form";
import rp, { AppComponent } from "@reporting/rp-app-host";
import { User, AccountingGroup } from "@reporting/rp-types/core";
import { UserFormAdapter, PasswordFormAdapter, ForcePwdChangeFormAdapter } from "../adapters";
import { TfaMethods, AppTFA, CallerContext } from "../types";

@customElement('rp-users-userform-container')
export class UserFormContainer extends AppComponent {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
		}
	`;

	@property({type: Object})
	user: User;

	@property({type: Object})
	group: AccountingGroup;

	@property({type: String})
	caller: CallerContext;

	@property({type: Boolean})
	loading: boolean = true;

	@query('og-overlay')
	loader: Overlay;

	appTFA: AppTFA = null;
	isPasswordChangeEnabled: boolean = false;
	isForcePwdChangeEnabled: boolean = false;
	isDeleteEnabled: boolean = false;
	isDomainChangeEnabled: boolean = false;

	async firstUpdated(): Promise<void> {
		this.loader.show(this);
		if(this.user) {
			const groups = await rp.ms('accounting').get<AccountingGroup[]>(`userGroups/${this.user.user_name}`);
			this.isForcePwdChangeEnabled = this.caller !== 'user';
			this.isPasswordChangeEnabled = await this.getPasswordChangeEnabled(groups);
			this.isDeleteEnabled = this.getDeleteEnabled(groups);
			if (this.user.tfa_method === TfaMethods.TFA_APP) {
				this.appTFA = await rp.ms('users').post<AppTFA>('appTFA', { user: this.user.user_name });
			}
		}
		this.loading = false;
		this.loader.hide();
	}

	save() {
		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		this.dispatchEvent(new Event('close'));
	}

	async getPasswordChangeEnabled(groups: AccountingGroup[]): Promise<boolean> {
		if (rp.userProvider.isSA()) return true;
		const allDisabled = groups.every(grp => !!grp.disable_password_change);
		const group = groups.find(g => g.id_group === rp.accountingProvider.accounting.id_accounting_group);
		return group?.password_complexity && !allDisabled;
	}

	getDeleteEnabled(groups: AccountingGroup[]): boolean {
		return groups.length <= 1 && this.user.user_name !== rp.userProvider.id
	}

	async toggleForcePwdChange(component: FormContainer, action: 'activate' | 'deactivate') {
		// update the button to show processing state
		component.options.buttons = [{
			key: 'dummy',
			text: msg('Processing...'),
			color: ColorTypes.Gray,
			icon: 'hourglass-half'
		}];
		component.requestUpdate();
		// Toggle the force password change state
		this.user = await rp.ms('users').post<User>('forcePasswordChange', {
			action,
			user: this.user.user_name,
			g: this.group.id_group
		});
		if (!this.user) {
			rp.host.bucket.showAlert({ content: msg('Failed to update force password change state.'), type: 'danger' });
			this.save();
		}
	}

	ogFormFactory(formId: string) {
		const component = dom.createElement<FormContainer>('og-form-container')
		switch (formId) {
			case 'user':
				component.adapter = new UserFormAdapter(this.group, this.user, this.caller, this.isDeleteEnabled);
				component.addEventListener('form.save', () => this.save());
				break;
			case 'password':
				component.adapter = new PasswordFormAdapter(this.group, this.user, this.caller);
				component.addEventListener('form.save', () => component.reset());
				break;
			case 'forceChange':
				component.adapter = new ForcePwdChangeFormAdapter(this.user);
				component.addEventListener('form.activate', () => this.toggleForcePwdChange(component, 'activate'));
				component.addEventListener('form.deactivate', () => this.toggleForcePwdChange(component, 'deactivate'));
				break;
		}
		return component;
	}

	getTfaForm() {
		const component = dom.createElement<HTMLDivElement>('div');
		component.innerHTML = `
			<img src="data:image/png;base64,${this.appTFA.qr}" alt="TFA QR Code" />
			<p>${msg('Scan the QR code with your TFA app to enable two-factor authentication.')}</p>
			<og-input value="${this.appTFA.tfa_secret}" style="width: 100%;"></og-input>
		`;
		component.style.height = '100%';
		component.style.padding = '0px 10px 20px';
		component.style.textAlign = 'center';
		return component;
	}

	layout() {}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		<og-container style="height: 100%; width: 100%">
			<div slot="content">
			${!this.loading ? html`
				${this.ogFormFactory('user')}
				${this.appTFA ? this.getTfaForm() : ''}
				${this.isPasswordChangeEnabled ? this.ogFormFactory('password') : ''}
				${this.isForcePwdChangeEnabled ? this.ogFormFactory('forceChange') : ''}
			` : ''}
			</div>
		</og-container>
	`;

}
